import { DateTime } from "luxon";
import { TIMELINE_PADDING } from "../core/layoutConstants";

type LineType = "line" | "header";
type Precision = "hour" | "30-minutes" | "10-minutes";

interface TimeLinesProps {
  readonly timeLineDateTimes: DateTime[];
  readonly scenarioStart: DateTime;
  readonly hourWidth: number;
  readonly lineType: LineType;
  readonly stageHeight?: number;
}

export function TimeLines({
  timeLineDateTimes,
  scenarioStart,
  hourWidth,
  lineType,
  stageHeight,
}: TimeLinesProps) {
  return (
    <>
      {timeLineDateTimes.map((dateTime) => (
        <TimeLine
          key={dateTime.toUnixInteger()}
          left={dateTime.diff(scenarioStart).as("hours") * hourWidth}
          dateTime={dateTime}
          lineType={lineType}
          height={stageHeight}
        />
      ))}
    </>
  );
}

interface TimeLineProps {
  readonly left: number;
  readonly dateTime: DateTime;
  readonly lineType: LineType;
  readonly height?: number;
}

function TimeLine({ left, dateTime, lineType, height }: TimeLineProps) {
  let precision: Precision;
  let text;
  if (dateTime.minute === 0) {
    precision = "hour";
    text = dateTime.toFormat("HH:mm");
  } else if (dateTime.minute === 30) {
    precision = "30-minutes";
  } else {
    precision = "10-minutes";
  }

  const topClass = getTopClass(lineType, precision);
  const bgClass = getBgClass(precision);

  return (
    <div
      data-precision={precision}
      data-linetype={lineType}
      className={`absolute flex h-full w-px items-center justify-center ${bgClass} ${topClass}`}
      style={{
        left: `${TIMELINE_PADDING + left}px`,
        minHeight: height ? `${height}px` : "80px",
      }}
    >
      {text !== "" && (
        <div className="font-weight-normal absolute top-[-17px] font-default text-xs">{text}</div>
      )}
    </div>
  );
}

function getBgClass(precision: Precision): string {
  switch (precision) {
    case "hour":
      return "bg-alpha-default";
    case "30-minutes":
    case "10-minutes":
      return "bg-alpha-subtle";
  }
}

function getTopClass(lineType: LineType, precision: Precision): string {
  if (lineType === "line") return "top-0";
  switch (precision) {
    case "hour":
    case "30-minutes":
      return "top-12";
    case "10-minutes":
      return "top-17";
  }
}
